import * as THREE from 'three';
import { setFakeLight } from './fakeLights';
import { IceCrystals } from './IceCrystals';
import { Shape, type Particles } from './Particles';
import { Elements } from './Elements';
import { KIND_COLOR, type Kind } from '../game/config';

const _c = new THREE.Color();
const rnd = (a: number, b: number) => a + Math.random() * (b - a);
const TAU = Math.PI * 2;
const _floor = new THREE.Vector3(), _k = new THREE.Color();

/** Pooled point lights for impact flashes (fixed count → no shader recompiles). */
class FlashLights {
  private lights: { l: THREE.PointLight; t: number; dur: number; peak: number }[] = [];
  constructor(scene: THREE.Scene, n: number) {
    for (let i = 0; i < n; i++) {
      // parameters only: the brightest active flash is fed to the shader-only light slot 1 (fx/fakeLights.ts)
      const l = new THREE.PointLight(0xffffff, 0, 3, 2);
      void scene;
      this.lights.push({ l, t: 1, dur: 1, peak: 0 });
    }
  }
  /** Soft ceiling per flash and a shared budget: impacts still light the scene, armour never burns out to white. */
  static readonly PEAK = 1.6;
  static readonly BUDGET = 2.4;

  fire(pos: THREE.Vector3, color: THREE.Color, peak: number, dist: number, dur: number) {
    peak = FlashLights.PEAK * (1 - Math.exp(-peak / FlashLights.PEAK));
    dist = Math.min(dist, 3.2);
    // reuse the weakest light
    let best = this.lights[0], bestI = Infinity;
    for (const s of this.lights) { const i = s.t >= s.dur ? -1 : s.peak * (1 - s.t / s.dur); if (i < bestI) { bestI = i; best = s; } }
    // lift the source above the blast: bodies standing in it get light, not a burnt-out white point
    best.l.position.set(pos.x, Math.max(pos.y, 0) + 1.2, pos.z); best.l.color.copy(color); best.l.distance = dist + 1.2;
    best.t = 0; best.dur = dur; best.peak = peak;
  }
  update(dt: number) {
    let total = 0;
    for (const s of this.lights) {
      if (s.t >= s.dur) { s.l.intensity = 0; continue; }
      s.t += dt;
      const k = Math.max(0, 1 - s.t / s.dur);
      s.l.intensity = s.peak * k * k;
      total += s.l.intensity;
    }
    // several blasts at once share one budget instead of stacking into a whiteout
    if (total > FlashLights.BUDGET) for (const s of this.lights) s.l.intensity *= FlashLights.BUDGET / total;
    let best = this.lights[0];
    for (const s of this.lights) if (s.l.intensity > best.l.intensity) best = s;
    setFakeLight(1, best.l.position, best.l.distance, best.l.color, best.l.intensity);
  }
}

/**
 * Effect recipes. Everything visual that "hits" goes through here so the
 * impact language (flash → shockwave → sparks → embers, plus shake / hit-stop)
 * stays consistent across the game.
 */
export class Fx {
  readonly lights: FlashLights;
  /** Camera trauma (0..1), squared for shake amplitude. */
  trauma = 0;
  /** Remaining hit-stop seconds; game time scales to ~0 while > 0. */
  hitstop = 0;
  /** Remaining slow-motion seconds (game time at ~30%). */
  slowmo = 0;
  /** Extra bloom intensity, decays to 0. */
  bloomKick = 0;
  /** Camera punch-in (0..1), decays to 0, toward `focus`. */
  punch = 0;
  readonly focus = new THREE.Vector3();

  /** Share of optional particles kept (quality tier, lowered further under sustained load). */
  density = 1;

  /** Graded elemental attack recipes (fire, ice, lightning, venom). */
  readonly el: Elements;

  constructor(readonly p: Particles, scene: THREE.Scene, flashLights = 3) {
    this.lights = new FlashLights(scene, flashLights);
    this.el = new Elements(p, (n) => this.n(n));
    this.crystals = new IceCrystals(scene);
    this.el.crystals = this.crystals;
  }

  /** Real faceted ice for the frost element (spikes, crowns, frost rings). */
  readonly crystals: IceCrystals;

  /** Scale a burst count by density with stochastic rounding, so small bursts keep their average. */
  private n(count: number) { return this.density >= 1 ? count : Math.floor(count * this.density + Math.random()); }

  /** Anticipation particles converge on the stone; no timers or added meshes. */
  prepare(kind: Kind, at: THREE.Vector3, duration: number, scale: number, level = 1) {
    if (kind === 't') {
      // A closing clock face underneath the orb, without the other elements' converging sparks.
      this.p.emit({ x: at.x, y: at.y - 0.18 * scale, z: at.z, color: _c.set(KIND_COLOR.t), intensity: 1.3,
        life: duration, size: 0.85 * scale, sizeEnd: 0.5 * scale, shape: Shape.Clock, fade: 0.15 });
      return;
    }
    const color = _k.set(KIND_COLOR[kind]);
    // higher grades gather visibly more energy before they strike
    const n = (kind === 'y' ? 5 : 3) + level;
    if (level >= 3) this.p.emit({ x: at.x, y: at.y, z: at.z, color: _c.set(KIND_COLOR[kind]), intensity: 0.6 + level * 0.25, life: duration, size: 0.1 * scale, sizeEnd: (0.3 + level * 0.06) * scale, shape: Shape.Glow, fade: 0.3 });
    for (let i = 0; i < n; i++) {
      const a = i / n * TAU, radius = (kind === 'y' ? 0.34 : 0.23) * scale;
      const dx = Math.cos(a) * radius, dz = Math.sin(a) * radius;
      this.p.emit({ x: at.x + dx, y: at.y + 0.04, z: at.z + dz, vx: -dx / duration, vz: -dz / duration,
        color, intensity: 1.6, life: duration, size: 0.075 * scale, sizeEnd: 0.025,
        shape: kind === 'b' ? Shape.Shard : kind === 'g' ? Shape.Bubble : kind === 'v' ? Shape.Drop : kind === 'y' || kind === 'a' || kind === 'o' || kind === 'k' || kind === 's' ? Shape.Spark : Shape.Flame,
        stretch: kind === 'y' ? 0.08 : 0, fade: 0.5 });
    }
  }

  launch(kind: Kind, at: THREE.Vector3, dir: THREE.Vector3, level: number, scale: number) {
    switch (kind) {
      case 'r':
        this.flare(at, 0xffad50, 0.35 * scale, 0.13, 2);
        this.sparks(at, 0xff6b24, 4 + level, 1.7, { dir, life: 0.2, gravity: 2, intensity: 2, size: 0.02 });
        break;
      case 'b':
        this.shards(at, 0xa6ddff, 3, 0.6, 1.8);
        this.flare(at, 0xbfe8ff, 0.28 * scale, 0.15, 1.8);
        break;
      case 'y':
        this.flare(at, 0xffe8a0, 0.65 * scale, 0.16, 2.3);
        this.sparks(at, 0xffce58, 5, 2.5, { dir, life: 0.16, gravity: 0, intensity: 2, size: 0.025 });
        break;
      case 'g':
        this.bubbles(at, 4, 0.12 * scale, 0.4);
        break;
      case 'a':
        this.flare(at, 0xe0c8ff, 0.3 * scale, 0.1, 2);
        break;
      case 'o':
        this.pop(at, KIND_COLOR.o, 0.25 * scale, 0.14, 1.8);
        break;
      case 'v':
        // A double heartbeat before the crimson thread returns.
        this.pop(at, KIND_COLOR.v, 0.2 * scale, 0.13, 1.7);
        this.p.emit({ x: at.x, y: at.y, z: at.z, color: _c.set(KIND_COLOR.v), intensity: 1.5,
          life: 0.18, size: 0.1 * scale, sizeEnd: 0.28 * scale, shape: Shape.Glow, delay: 0.11, fade: 1 });
        break;
      case 't':
        this.p.emit({ x: at.x, y: at.y - 0.17 * scale, z: at.z, color: _c.set(KIND_COLOR.t), intensity: 1.3,
          life: 0.3, size: 0.5 * scale, sizeEnd: 0.95 * scale, shape: Shape.Clock, fade: 1.2 });
        break;
      case 'k':
        this.flare(at, 0xffb070, 0.3 * scale, 0.12, 2);
        this.sparks(at, KIND_COLOR.k, 3, 1.6, { dir, life: 0.18, gravity: 0, intensity: 2, size: 0.02 });
        break;
      case 's':
        this.flare(at, 0xd8fbff, 0.4 * scale, 0.12, 2.4);
        this.sparks(at, KIND_COLOR.s, 4, 2.2, { dir, life: 0.16, gravity: 0, intensity: 2.2, size: 0.022 });
        break;
    }
  }

  bubbles(at: THREE.Vector3, n: number, radius: number, life = 0.65) {
    for (let i = 0, m = this.n(n); i < m; i++) {
      const a = Math.random() * TAU, r = rnd(0.2, 1) * radius;
      this.p.emit({ x: at.x + Math.cos(a) * r, y: at.y, z: at.z + Math.sin(a) * r,
        vx: Math.cos(a) * 0.15, vy: rnd(0.25, 0.6), vz: Math.sin(a) * 0.15,
        color: _c.set(0x8adb55), intensity: 1.25, life: life * rnd(0.7, 1.2), size: rnd(0.075, 0.13), sizeEnd: 0.02,
        drag: 1.6, shape: Shape.Bubble, fade: 1.1 });
    }
  }

  burning(at: THREE.Vector3, radius: number, strength: number) {
    // the ground itself is drawn by the scorch mesh; here only low tongues of fire and a little smoke
    if (Math.random() < 0.35 * strength) {
      const a=Math.random()*TAU,r=Math.sqrt(Math.random())*radius*0.7;
      this.el.smoke(at.x+Math.cos(a)*r, 0.15, at.z+Math.sin(a)*r, 0, 0.45, 0, 0.2, 0.6, 1.2, 0x2a2420, 0.35, 0);
    }
    for(let i=0;i<3;i++) {
      const a=Math.random()*TAU,r=Math.sqrt(Math.random())*radius;
      this.p.emit({x:at.x+Math.cos(a)*r,y:at.y+0.08,z:at.z+Math.sin(a)*r,vy:0.35,color:_c.set(0xff882c),intensity:1.25*strength,life:0.42,size:0.2,sizeEnd:0.05,shape:Shape.Flame});
    }
  }

  iceEruption(at: THREE.Vector3, level: number) {
    this.impact('b', at, level, 0.55 + level * 0.07);
    this.el.iceEruption(at, level);
  }

  /** A falling star hits the road: crater flash, fire column, molten debris, shockwave and dust. */
  meteorImpact(at: THREE.Vector3) {
    this.impact('r', at, 5, 1.5);
    _floor.set(at.x, 0.03, at.z);
    this.pop(at, 0xffe2b0, 1.3, 0.12, 4);
    this.floorFlash(_floor, 0xff5a1a, 1.5, 0.5, 2.6);
    this.ring(_floor, 0xffb070, 1.9, 0.4, 3);
    this.ring(_floor, 0xff6a2a, 1.2, 0.55, 1.6, 0.06);
    this.el.fireballImpact(at, 1.2);
    for (let i = 0; i < 10; i++) {
      const a = Math.random() * TAU, sp = rnd(1.4, 3.2);
      this.p.emit({ x: at.x, y: 0.15, z: at.z, vx: Math.cos(a) * sp, vy: rnd(2.6, 4.6), vz: Math.sin(a) * sp,
        color: _c.set(i % 3 ? 0xff7a2a : 0xffc070), intensity: 2.4, life: rnd(0.55, 0.85), size: rnd(0.05, 0.09), sizeEnd: 0.02,
        gravity: 11, drag: 0.4, shape: Shape.Shard, stretch: 0.04, fade: 0.6 });
    }
    this.dust(_floor, 0.5, 8, 0.55);
    this.flash(at, 0xff7a30, 12, 4, 0.3);
    this.shake(0.15);
  }

  /** The instant of contact on an enemy: a short white-hot glint with element-coloured chips. */
  hit(kind: Kind, at: THREE.Vector3, scale = 1) {
    const col = KIND_COLOR[kind];
    this.p.emit({ x: at.x, y: at.y, z: at.z, color: _k.set(col).lerp(_c.set(0xffffff), 0.6), intensity: 3.2,
      life: 0.09, size: 0.12 * scale, sizeEnd: 0.42 * scale, shape: Shape.Flare, fade: 1.4 });
    this.sparks(at, col, 3, 1.8, { up: 0.5, life: 0.18, size: 0.022, intensity: 2.6, gravity: 4 });
  }

  /** Every element has its own ground mark, debris and decay envelope. */
  impact(kind: Kind, at: THREE.Vector3, level: number, scale = 1) {
    _floor.set(at.x, 0.035, at.z);
    switch (kind) {
      case 'r':
        this.pop(at, 0xffb45a, 0.35 * scale, 0.1, 2.8);
        this.floorFlash(_floor, 0xff491a, (0.55 + level * 0.06) * scale, 0.32, 1.5);
        this.sparks(at, 0xff7a2c, Math.ceil((7 + level * 2) * scale), 2.8, { up: 0.35, life: 0.35, intensity: 2.2 });
        for (let i = 0; i < Math.ceil((3 + level) * scale); i++) {
          this.p.emit({ x: at.x + rnd(-0.16, 0.16), y: at.y - 0.15, z: at.z + rnd(-0.16, 0.16),
            vx: rnd(-0.3, 0.3), vy: rnd(0.8, 1.4), vz: rnd(-0.3, 0.3), color: _c.set(0xff641f),
            intensity: 1.6, life: rnd(0.3, 0.5), size: rnd(0.16, 0.24) * scale, sizeEnd: 0.03,
            shape: Shape.Flame, drag: 2, fade: 1.2 });
        }
        this.flash(at, 0xff7029, 3.5 * scale, 2.2, 0.18);
        break;
      case 'b':
        this.flare(at, 0xc6eeff, 0.45 * scale, 0.12, 2.2);
        this.shards(at, 0x8bd1ff, Math.ceil((7 + level) * scale), 1.8, 2);
        this.p.emit({ x: at.x, y: _floor.y, z: at.z, color: _c.set(0x74c3ff), intensity: 1.7,
          life: 0.85, size: 0.8 * scale, sizeEnd: (1.6 + level * 0.08) * scale, shape: Shape.Frost, fade: 1.1 });
        this.flash(at, 0x80caff, 2 * scale, 1.8, 0.16);
        break;
      case 'y':
        this.flare(at, 0xffe7a0, 0.65 * scale, 0.16, 2.4);
        this.sparks(at, 0xffd24a, Math.ceil((4 + level) * scale), 2.3, { life: 0.22, gravity: 0, intensity: 2 });
        this.p.emit({ x: at.x, y: _floor.y, z: at.z, color: _c.set(0xffcd56), intensity: 1.8,
          life: 0.38, size: 0.7 * scale, sizeEnd: (1.3 + level * 0.05) * scale, shape: Shape.Sun, fade: 1.4 });
        this.flash(at, 0xffd26b, 2.5 * scale, 1.8, 0.12);
        break;
      case 'g': {
        const radius = (0.7 + level * 0.08) * scale;
        // splash wave racing out over the new puddle, a lime pop and a floor glow
        this.p.emit({ x: at.x, y: _floor.y + 0.008, z: at.z, color: _c.set(0x51bc42), intensity: 1.2,
          life: 0.5, size: radius * 0.6, sizeEnd: radius * 2.1, shape: Shape.Venom, fade: 1.6 });
        this.pop(at, 0xb6ff7a, (0.45 + level * 0.05) * scale, 0.12, 2.4);
        this.floorFlash(_floor, 0x5ad673, radius * 1.2, 0.3, 1.1);
        this.bubbles(_floor, Math.ceil((2 + level) * scale), radius * 0.6);
        // dark droplets thrown out, stains where they land, toxic cloud from grade III
        this.el.venomSplash(at, level, radius, scale);
        break;
      }
      case 'a':
        // a small, crisp arcane hit: a pop, a few motes and a thin ring
        this.pop(at, 0xe6d4ff, 0.3 * scale, 0.08, 2.6);
        this.sparks(at, 0xb27cff, Math.ceil((3 + level) * scale), 1.8, { life: 0.22, gravity: 0, intensity: 2, size: 0.022 });
        this.ring(at, 0xb27cff, 0.35 + level * 0.04, 0.2, 2);
        break;
      case 'k':
        // the mark is struck: a sharp ember pop and a target ring closing on the body
        this.pop(at, 0xffc090, 0.3 * scale, 0.08, 2.6);
        this.ring(at, KIND_COLOR.k, (0.55 + level * 0.05) * scale, 0.28, 2.2);
        this.sparks(at, KIND_COLOR.k, Math.ceil((3 + level) * scale), 1.5, { life: 0.2, gravity: 2, intensity: 2, size: 0.02 });
        break;
      case 's':
        // a crack of storm light, bigger the more the eye has gathered (scale carries it)
        this.pop(at, 0xe6feff, 0.32 * scale, 0.08, 2.8);
        this.sparks(at, KIND_COLOR.s, Math.ceil((4 + level) * scale), 2.4, { life: 0.2, gravity: 0, intensity: 2.4, size: 0.022 });
        this.flash(at, KIND_COLOR.s, 2 * scale, 1.6, 0.12);
        break;
    }
  }

  /** Users who ask for reduced motion (the system, or «Менше руху») still get the hit, at a third of the camera motion. */
  motion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches ? 0.35 : 1;

  shake(amount: number) { this.trauma = Math.min(1, this.trauma + amount * this.motion); }
  stop(seconds: number) { this.hitstop = Math.max(this.hitstop, seconds); }
  slow(seconds: number) { this.slowmo = Math.max(this.slowmo, seconds); }
  bloom(amount: number) { this.bloomKick = Math.max(this.bloomKick, amount); }
  zoom(amount: number, at: THREE.Vector3) { this.punch = Math.min(1, Math.max(this.punch, amount * this.motion)); this.focus.copy(at); }

  /** Game-time multiplier from hit-stop / slow-motion. */
  get timeScale() { return this.hitstop > 0 ? 0.05 : this.slowmo > 0 ? 0.3 : 1; }

  /** Four-point star glint. */
  flare(pos: THREE.Vector3, color: THREE.ColorRepresentation, size: number, life = 0.3, intensity = 3) {
    this.p.emit({ x: pos.x, y: pos.y, z: pos.z, color: _c.set(color), intensity, life, size: size * 0.3, sizeEnd: size, shape: Shape.Flare, fade: 1.6 });
  }

  update(dt: number) {
    this.lights.update(dt);
    this.trauma = Math.max(0, this.trauma - dt * 1.6);
    this.hitstop = Math.max(0, this.hitstop - dt);
    this.slowmo = Math.max(0, this.slowmo - dt);
    this.bloomKick = Math.max(0, this.bloomKick - dt * (1 + this.bloomKick * 2));
    this.punch = Math.max(0, this.punch - dt * 1.8);
  }

  flash(pos: THREE.Vector3, color: THREE.ColorRepresentation, peak = 6, dist = 3, dur = 0.25) {
    this.lights.fire(pos, _c.set(color), peak, dist, dur);
  }

  /** Radial spark burst (velocity-stretched streaks). */
  sparks(pos: THREE.Vector3, color: THREE.ColorRepresentation, n: number, speed: number, o: { up?: number; spread?: number; life?: number; size?: number; gravity?: number; intensity?: number; dir?: THREE.Vector3 } = {}) {
    const col = _k.set(color);
    for (let i = 0, m = this.n(n); i < m; i++) {
      const a = Math.random() * TAU, u = rnd(-1, 1) * (o.spread ?? 1);
      let vx = Math.cos(a) * Math.sqrt(1 - u * u), vz = Math.sin(a) * Math.sqrt(1 - u * u), vy = Math.abs(u) + (o.up ?? 0.4);
      if (o.dir) { vx += o.dir.x * 1.5; vy += o.dir.y * 1.5; vz += o.dir.z * 1.5; }
      const s = speed * rnd(0.35, 1);
      this.p.emit({
        x: pos.x, y: pos.y, z: pos.z, vx: vx * s, vy: vy * s, vz: vz * s,
        color: col, intensity: (o.intensity ?? 3) * rnd(0.6, 1.4), life: (o.life ?? 0.45) * rnd(0.6, 1.3),
        size: (o.size ?? 0.035) * rnd(0.7, 1.3), sizeEnd: 0, gravity: o.gravity ?? 6, drag: 3.2, shape: Shape.Spark, stretch: 0.045, fade: 1,
      });
    }
  }

  /** Soft glowing embers drifting up. */
  embers(pos: THREE.Vector3, color: THREE.ColorRepresentation, n: number, o: { radius?: number; rise?: number; life?: number; size?: number; intensity?: number } = {}) {
    const col = _k.set(color);
    for (let i = 0, m = this.n(n); i < m; i++) {
      const a = Math.random() * TAU, r = Math.sqrt(Math.random()) * (o.radius ?? 0.3);
      this.p.emit({
        x: pos.x + Math.cos(a) * r, y: pos.y + rnd(-0.05, 0.1), z: pos.z + Math.sin(a) * r,
        vx: rnd(-0.3, 0.3), vy: (o.rise ?? 1) * rnd(0.5, 1.4), vz: rnd(-0.3, 0.3),
        color: col, intensity: (o.intensity ?? 2) * rnd(0.6, 1.3), life: (o.life ?? 0.9) * rnd(0.6, 1.3),
        size: (o.size ?? 0.06) * rnd(0.6, 1.3), sizeEnd: 0, gravity: -0.4, drag: 1.5, shape: Shape.Glow, fade: 1.4,
      });
    }
  }

  /** Expanding ground shockwave ring. */
  ring(pos: THREE.Vector3, color: THREE.ColorRepresentation, radius: number, life = 0.45, intensity = 3, delay = 0) {
    this.p.emit({ x: pos.x, y: pos.y + 0.02, z: pos.z, color: _c.set(color), intensity, life, size: radius * 0.15, sizeEnd: radius, shape: Shape.Ring, fade: 1.6, delay });
  }

  /** Flat light splash on the floor. */
  floorFlash(pos: THREE.Vector3, color: THREE.ColorRepresentation, radius: number, life = 0.35, intensity = 2) {
    this.p.emit({ x: pos.x, y: pos.y + 0.015, z: pos.z, color: _c.set(color), intensity, life, size: radius * 0.6, sizeEnd: radius, shape: Shape.Floor, fade: 2 });
  }

  /** Billboard glow pop (core of every flash). */
  pop(pos: THREE.Vector3, color: THREE.ColorRepresentation, size: number, life = 0.18, intensity = 4) {
    this.p.emit({ x: pos.x, y: pos.y, z: pos.z, color: _c.set(color), intensity, life, size, sizeEnd: size * 1.6, shape: Shape.Glow, fade: 2 });
  }

  beam(pos: THREE.Vector3, color: THREE.ColorRepresentation, width: number, height: number, life = 0.5, intensity = 3) {
    this.p.emit({ x: pos.x, y: pos.y, z: pos.z, color: _c.set(color), intensity, life, size: width, sizeEnd: width * 0.2, shape: Shape.Beam, stretch: height, fade: 1.3 });
  }

  shards(pos: THREE.Vector3, color: THREE.ColorRepresentation, n: number, speed: number, intensity = 2.5) {
    const col = _k.set(color);
    for (let i = 0, m = this.n(n); i < m; i++) {
      const a = Math.random() * TAU, s = speed * rnd(0.4, 1);
      this.p.emit({
        x: pos.x, y: pos.y, z: pos.z, vx: Math.cos(a) * s, vy: rnd(0.8, 2.2), vz: Math.sin(a) * s,
        color: col, intensity: intensity * rnd(0.7, 1.3), life: rnd(0.35, 0.6), size: rnd(0.04, 0.07), sizeEnd: 0.01,
        gravity: 9, drag: 2, shape: Shape.Shard, stretch: 0.03, fade: 0.8,
      });
    }
  }

  /** Poison / smoke style puffs (additive, low intensity). */
  puffs(pos: THREE.Vector3, color: THREE.ColorRepresentation, n: number, radius: number, intensity = 0.6) {
    const col = _k.set(color);
    for (let i = 0, m = this.n(n); i < m; i++) {
      const a = Math.random() * TAU, r = Math.sqrt(Math.random()) * radius;
      this.p.emit({
        x: pos.x + Math.cos(a) * r, y: pos.y + rnd(0, 0.2), z: pos.z + Math.sin(a) * r,
        vx: Math.cos(a) * 0.4, vy: rnd(0.2, 0.6), vz: Math.sin(a) * 0.4,
        color: col, intensity: intensity * rnd(0.6, 1.2), life: rnd(0.7, 1.2), size: rnd(0.2, 0.32), sizeEnd: rnd(0.45, 0.6),
        drag: 2, shape: Shape.Glow, fade: 1.5, delay: rnd(0, 0.12),
      });
    }
  }

  /** Low ground dust kicked up by a footstep (dim, slow, spreads outward). */
  dust(pos: THREE.Vector3, radius: number, n: number, intensity = 0.4) {
    for (let i = 0, m = this.n(n); i < m; i++) {
      const a = Math.random() * TAU, r = Math.sqrt(Math.random()) * radius;
      this.p.emit({
        x: pos.x + Math.cos(a) * r, y: pos.y + rnd(0, 0.05), z: pos.z + Math.sin(a) * r,
        vx: Math.cos(a) * rnd(0.3, 0.7), vy: rnd(0.08, 0.3), vz: Math.sin(a) * rnd(0.3, 0.7),
        color: _c.set(0x6b5440), intensity: intensity * rnd(0.6, 1.1), life: rnd(0.45, 0.8),
        size: rnd(0.1, 0.16), sizeEnd: rnd(0.28, 0.4), drag: 3.5, shape: Shape.Glow, fade: 1.6,
      });
    }
  }

  /** A trail sample left behind a moving object (call every frame). */
  trail(pos: THREE.Vector3, color: THREE.ColorRepresentation, size: number, intensity = 1.5, life = 0.25) {
    this.p.emit({ x: pos.x + rnd(-0.01, 0.01), y: pos.y + rnd(-0.01, 0.01), z: pos.z + rnd(-0.01, 0.01), vx: rnd(-0.15, 0.15), vy: rnd(0, 0.25), vz: rnd(-0.15, 0.15), color: _c.set(color), intensity, life, size, sizeEnd: 0, drag: 2, fade: 1.2 });
  }

}
